import { initializeLogger, analytics, operational } from "@teamstelemetry/shared-logger";
import { telemetryManager } from './telemetryManager';
import {
  getUserContextProvider,
  SessionContextProvider
} from "@teamstelemetry/shared-logger";
import { logDev } from '@/logging/devConsole';

type SharedLogger = ReturnType<typeof analytics>["loggers"] & ReturnType<typeof operational>["loggers"];
type TelemetryEnvironment = "prod" | "eu";
type RegionConfiguration = {
  region?: string;
};
type RegionInput = RegionConfiguration | string | undefined;

const DEFAULT_ENVIRONMENT: TelemetryEnvironment = "eu";

// EUDB regions: EMEA, CH, FR, DE, NO, SE. EU routing is compliant for all (safer default).
const EUDB_REGIONS = new Set([
  "EMEA", "CH", "FR", "DE", "NO", "SE"
]);

const SESSION_ID_KEY = "byodWizardSessionId";

// Persist session ID across auth redirects and page navigations
function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    // Generate a new session ID using crypto.randomUUID or fallback
    sessionId = typeof crypto !== 'undefined' && crypto.randomUUID 
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    logDev('[Telemetry] Created new session ID:', sessionId);
  } else {
    logDev('[Telemetry] Restored session ID from sessionStorage:', sessionId);
  }
  return sessionId;
}

const userProvider = getUserContextProvider();
const sessionProvider = new SessionContextProvider(getOrCreateSessionId());

let activeEnvironment: TelemetryEnvironment = DEFAULT_ENVIRONMENT;

function createLogger(environment: TelemetryEnvironment): SharedLogger {
  return initializeLogger({
    iKey: "78e67a463a3e4a5c84eea1a09103b216-26ed6211-e8e6-4d01-8db0-f9d2bee5ad99-7196",
    name: "Teams BYOD Wizard Prod",
    initializers: [analytics, operational],
    contextProviders: [sessionProvider, userProvider],
    settings: {
      logging: true,
      environment
    }
  }) as SharedLogger;
}

let logger: SharedLogger = createLogger(activeEnvironment);
let lastTenantId: string | undefined;
let lastUserId: string | undefined;

function isEudbRegion(region?: string): boolean {
  if (!region) return false;
  return EUDB_REGIONS.has(region.trim().toUpperCase());
}

function resolveConfig(input: RegionInput): RegionConfiguration {
  if (!input) {
    return {};
  }

  if (typeof input === "string") {
    return { region: input };
  }

  return input;
}

function shouldUseEuEnvironment(input: RegionInput): boolean {
  const config = resolveConfig(input);
  if (!config.region) return true; // Safe default for compliance
  return EUDB_REGIONS.has(config.region.trim().toUpperCase());
}

export function setLoggerEnvironment(environment: TelemetryEnvironment): void {
  if (environment === activeEnvironment) {
    return;
  }

  logDev(`[EUDB Telemetry] Switching to '${environment}' environment (from '${activeEnvironment}')`);

  logger = createLogger(environment);
  activeEnvironment = environment;
  telemetryManager.rehydrateActiveScenario();
  
  if (lastTenantId) {
    try {
      userProvider.update({
        "UserInfo.TenantId": lastTenantId
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[telemetry] Failed to reapply tenant context after reinit', error);
      }
    }
  }
  if (lastUserId) {
    try {
      userProvider.update({
        "UserInfo.Id": lastUserId
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[telemetry] Failed to reapply user id context after reinit', error);
      }
    }
  }
}

/**
 * Configure telemetry logger for EUDB compliance.
 * Routes EMEA, CH, FR, DE, NO, SE to EU telemetry. Default is EU for compliance.
 * 
 * @param _input - Region from tenant geo lookup
 * @returns Active telemetry environment ('eu' or 'prod')
 * @see https://aka.ms/teamscoresvcseudb
 */
export function configureLoggerForRegion(_input?: RegionInput): TelemetryEnvironment {
  const desiredEnvironment: TelemetryEnvironment = shouldUseEuEnvironment(_input) ? "eu" : "prod";
  
  const config = resolveConfig(_input);
  logDev(`[EUDB Telemetry] Region: ${config.region || 'unknown'} -> Environment: ${desiredEnvironment}`);
  
  setLoggerEnvironment(desiredEnvironment);
  return activeEnvironment;
}

export function updateTelemetryTenant(tenantId?: string): void {
  if (!tenantId) {
    return;
  }

  try {
    userProvider.update({
      "UserInfo.TenantId": tenantId
    });
    telemetryManager.collectUserIdentity({ tenantId });
    lastTenantId = tenantId;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[telemetry] Failed to update user tenant context", error);
    }
  }
}

export function updateTelemetryUserId(userId?: string): void {
  if (!userId) {
    return;
  }

  try {
    userProvider.update({
      "UserInfo.Id": userId
    });
    telemetryManager.collectUserIdentity({ userId });
    lastUserId = userId;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[telemetry] Failed to update user id context", error);
    }
  }
}

export function getLogger(): SharedLogger {
  return logger;
}

export { userProvider as userContextProvider };

export function __resetTelemetryForTests(): void {
  logger = createLogger(DEFAULT_ENVIRONMENT);
  activeEnvironment = DEFAULT_ENVIRONMENT;
  lastTenantId = undefined;
  lastUserId = undefined;
  sessionStorage.removeItem(SESSION_ID_KEY);
}

export function getLastIdentity() {
  return { tenantId: lastTenantId, userId: lastUserId };
}

export function getByodSessionId(): string | null {
  return sessionStorage.getItem(SESSION_ID_KEY);
}

export default logger;
